// OpenFinance-OS Brand Theme
// Based on official brand guidelines v1.0

export interface OpenFinanceTheme {
  name: string
  colors: {
    // Primary Teal Scale
    primary50: string
    primary100: string
    primary200: string
    primary300: string
    primary400: string
    primary500: string
    primary600: string
    primary700: string
    primary800: string
    primary900: string

    // Accent Amber
    accent300: string
    accent500: string
    accent600: string

    // Neutrals
    text: string
    textLight: string
    textMuted: string
    background: string
    cardBg: string
    border: string
    borderLight: string
  }
  gradients: {
    primary: string
    accent: string
    card: string
    header: string
  }
  fonts: {
    display: string // Fraunces
    body: string // DM Sans
    mono: string // JetBrains Mono
  }
  spacing: {
    xs: string // 4px
    sm: string // 8px
    md: string // 16px
    lg: string // 24px
    xl: string // 32px
    '2xl': string // 48px
  }
  radius: {
    sm: string // 6px - tags, small buttons
    md: string // 10px - buttons, inputs
    lg: string // 16px - cards
    xl: string // 24px - modals, large panels
  }
  shadows: {
    sm: string
    md: string
    lg: string
    xl: string
  }
}

export const OPENFINANCE_THEME: OpenFinanceTheme = {
  name: 'OpenFinance-OS',
  colors: {
    // Primary Teal Scale (from brand guidelines)
    primary50: '#E6F2F2',
    primary100: '#B3D9D9',
    primary200: '#80C0C0',
    primary300: '#4DA6A6',
    primary400: '#1A6B6B',
    primary500: '#0D4F4F', // Main brand color
    primary600: '#0A4040',
    primary700: '#083838',
    primary800: '#062B2B',
    primary900: '#041E1E',

    // Accent Amber (from brand guidelines)
    accent300: '#F5C989',
    accent500: '#E8A85C', // Main accent color
    accent600: '#D4893A',

    // Neutrals
    text: '#1A1A1A',
    textLight: '#4A4A4A',
    textMuted: '#6B7280',
    background: '#F9FAFB',
    cardBg: '#FFFFFF',
    border: '#E5E7EB',
    borderLight: '#F3F4F6',
  },
  gradients: {
    primary: 'linear-gradient(135deg, #0D4F4F 0%, #1A6B6B 100%)',
    accent: 'linear-gradient(135deg, #E8A85C 0%, #F5C989 100%)',
    card: 'linear-gradient(135deg, #E6F2F2 0%, #FFFFFF 100%)',
    header: 'linear-gradient(135deg, #083838 0%, #0D4F4F 100%)',
  },
  fonts: {
    display: "'Fraunces', Georgia, serif",
    body: "'DM Sans', system-ui, -apple-system, sans-serif",
    mono: "'JetBrains Mono', Consolas, monospace",
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
  },
  radius: {
    sm: '6px', // Tags, small buttons
    md: '10px', // Buttons, inputs
    lg: '16px', // Cards, containers
    xl: '24px', // Modals, large panels
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(13, 79, 79, 0.05)',
    md: '0 4px 6px -1px rgba(13, 79, 79, 0.1), 0 2px 4px -1px rgba(13, 79, 79, 0.06)',
    lg: '0 10px 15px -3px rgba(13, 79, 79, 0.1), 0 4px 6px -2px rgba(13, 79, 79, 0.05)',
    xl: '0 20px 25px -5px rgba(13, 79, 79, 0.1), 0 10px 10px -5px rgba(13, 79, 79, 0.04)',
  },
}

// Logo URLs (from uploaded images)
export const OPENFINANCE_LOGOS = {
  icon32:
    'https://cdn.magicpatterns.com/uploads/nqVPVd4mpACYnsiYHphsMF/logo-icon-32.png',
  icon64:
    'https://cdn.magicpatterns.com/uploads/a1E4LBQ4TrcPzKqd7QWoLQ/logo-icon-64.png',
  icon128:
    'https://cdn.magicpatterns.com/uploads/raBpLfvMs63opF5GLz5QE2/logo-icon-128.png',
  icon256:
    'https://cdn.magicpatterns.com/uploads/dhPqACQoa4n95qbEDutMtc/logo-icon-256.png',
  icon512:
    'https://cdn.magicpatterns.com/uploads/pgCGihfiMBV6u2TAQ9mpZ5/logo-icon-512.png',
}

// Helper to get appropriate logo size
export const getOpenFinanceLogo = (
  size: 'sm' | 'md' | 'lg' | 'xl' = 'md',
): string => {
  switch (size) {
    case 'sm':
      return OPENFINANCE_LOGOS.icon64
    case 'md':
      return OPENFINANCE_LOGOS.icon128
    case 'lg':
      return OPENFINANCE_LOGOS.icon256
    case 'xl':
      return OPENFINANCE_LOGOS.icon512
    default:
      return OPENFINANCE_LOGOS.icon128
  }
}
